import { memo, useCallback, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"

import { FeedbackOriginTextRecord } from "@productlane/lib"
import { Button, PriorityNaming } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { FilterType } from "./filter-popover-items/types"
import type { ThreadsFilterNegationKey } from "./threads-filter-state"
import type { FilterChip } from "@/components/filters"
import { FilterControlChips } from "@/components/filters"
import { generateId } from "@/lib/id"
import { MAIN_PORTAL_LABEL } from "@/lib/portal-instances"
import { useSlackWorkspaceOptions } from "@/lib/slack-workspaces"
import { useThreadState } from "@/lib/states/global-state"
import { useInboxAddresses } from "@/lib/use-inbox-addresses"
import { usePortalMarkById } from "@/lib/use-portal-marks"
import { useZero } from "@/lib/zero"
import { AssigneesFilterItems } from "./filter-popover-items/assignees-filter-items"
import { CompanyOwnerFilterItems } from "./filter-popover-items/company-owner-filter-items"
import { CompanyStatusFilterItems } from "./filter-popover-items/company-status-filter-items"
import { CompanyTierFilterItems } from "./filter-popover-items/company-tier-filter-items"
import { CreatorFilterItems } from "./filter-popover-items/creator-filter-items"
import { InboxFilterItems } from "./filter-popover-items/inbox-filter-items"
import { IssueStatusFilterItems } from "./filter-popover-items/issue-status-filter-items"
import { LinkedCompaniesFilterItems } from "./filter-popover-items/linked-companies-filter-items"
import { LinkedIssuesFilterItems } from "./filter-popover-items/linked-issues-filter-items"
import { LinkedPersonsFilterItems } from "./filter-popover-items/linked-persons-filter-items"
import { LinkedProjectsFilterItems } from "./filter-popover-items/linked-projects-filter-items"
import { LinkedTagsFilterItems } from "./filter-popover-items/linked-tags-filter-items"
import { OriginFilterItems } from "./filter-popover-items/origin-filter-items"
import { PortalFilterItems } from "./filter-popover-items/portal-filter-items"
import { PriorityFilterItems } from "./filter-popover-items/priority-filter-items"
import {
	ReplyStateFilterItems,
	ReplyStateNaming,
} from "./filter-popover-items/reply-state-filter-items"
import { SlackChannelFilterItems } from "./filter-popover-items/slack-channel-filter-items"
import { SlackWorkspaceFilterItems } from "./filter-popover-items/slack-workspace-filter-items"
import {
	StatusFilterItems,
	StatusNaming,
} from "./filter-popover-items/status-filter-items"
import { useThreadsFilterState } from "./threads-filter-state"

const CATEGORIES_WITH_CUSTOM_FILTER: FilterType[] = [
	"Projects",
	"Tags",
	"Issues",
	"Companies",
	"People",
	"SlackChannels",
]

export const ThreadsFilterControl = memo(function ThreadsFilterControl({
	embedded = false,
}: {
	/** Inside the edit view dialog: no frame, and no Save as view button. */
	embedded?: boolean
} = {}) {
	const zero = useZero()
	const navigate = useNavigate()
	const {
		inboxState,
		setInboxState,
		setActiveCustomViewId,
		setPendingEditCustomViewId,
		isEditingCustomView,
	} = useThreadState()

	const [users] = useQuery(queries.users())
	const [projects] = useQuery(queries.projects())
	const [tags] = useQuery(queries.tags())
	const [customViews] = useQuery(queries.customViews())
	const portalMarkById = usePortalMarkById()
	const inboxAddresses = useInboxAddresses()

	const {
		companyIds,
		personIds,
		projectIds,
		tagIds,
		issueIds,
		assigneeIds,
		creatorIds,
		painLevels,
		origins,
		portalInstanceIds,
		slackWorkspaceIds,
		slackChannelIds,
		emailAddressIds,
		statuses,
		replyStates,
		companyTiers,
		companyStatuses,
		companyOwnerIds,
		issueStatuses,
		companyRevenue,
		companySize,
		negations,
		setNegation,
		clearAssignees,
		clearCompanies,
		clearPersons,
		clearProjects,
		clearTags,
		clearIssues,
		clearCreators,
		clearOrigins,
		clearPortalInstances,
		clearSlackWorkspaces,
		clearSlackChannels,
		clearEmailAddresses,
		clearPainLevels,
		clearStatuses,
		clearReplyStates,
		clearCompanyTiers,
		clearCompanyStatuses,
		clearCompanyOwners,
		clearIssueStatuses,
		setCompanyRevenue,
		setCompanySize,
	} = useThreadsFilterState()
	const chipIssueId = issueIds.length === 1 ? issueIds[0] : null
	const [chipIssue] = useQuery(queries.issueById(chipIssueId ?? ""), {
		enabled: !!chipIssueId,
	})
	const chipCompanyId = companyIds.length === 1 ? companyIds[0] : null
	const chipPersonId = personIds.length === 1 ? personIds[0] : null
	const [chipCompany] = useQuery(queries.companyById(chipCompanyId ?? ""), {
		enabled: !!chipCompanyId,
	})
	const [chipPerson] = useQuery(queries.contactById(chipPersonId ?? ""), {
		enabled: !!chipPersonId,
	})

	const [query, setQuery] = useState("")
	const slackWorkspaces = useSlackWorkspaceOptions()
	const [slackChannelRows] = useQuery(
		queries.slackChannelsByChannelIds({ channelIds: slackChannelIds }),
		{ enabled: slackChannelIds.length > 0 },
	)

	const negationProps = (
		key: ThreadsFilterNegationKey,
		valueCount: number,
	) => ({
		valueCount,
		isNegated: negations[key] ?? false,
		onNegatedChange: (isNegated: boolean) => {
			setNegation(key, isNegated)
		},
	})

	const onFilterItemSelect = () => {
		setTimeout(() => setQuery(""), 100)
	}

	const renderEditor = (label: FilterType) => (
		<Command shouldFilter={!CATEGORIES_WITH_CUSTOM_FILTER.includes(label)}>
			<CommandInput
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[70dvh]">
				<CommandEmpty>Nothing found.</CommandEmpty>
				<CommandGroup>
					{label === "Assignees" && (
						<AssigneesFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Origins" && (
						<OriginFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Portals" && (
						<PortalFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "SlackWorkspaces" && (
						<SlackWorkspaceFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "SlackChannels" && (
						<SlackChannelFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{label === "Inboxes" && (
						<InboxFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Creators" && (
						<CreatorFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Priorities" && (
						<PriorityFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Statuses" && (
						<StatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "ReplyStates" && (
						<ReplyStateFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Projects" && (
						<LinkedProjectsFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{label === "Tags" && (
						<LinkedTagsFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{label === "Issues" && (
						<LinkedIssuesFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{label === "Companies" && (
						<LinkedCompaniesFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{label === "People" && (
						<LinkedPersonsFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{label === "IssueStatuses" && (
						<IssueStatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "CompanyTiers" && (
						<CompanyTierFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "CompanyStatuses" && (
						<CompanyStatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "CompanyOwners" && (
						<CompanyOwnerFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)

	const chips = useMemo<FilterChip[]>(() => {
		const result: FilterChip[] = []

		if (companyIds.length > 0) {
			result.push({
				key: "Companies",
				label: "Companies",
				...negationProps("companyIds", companyIds.length),
				valueText:
					companyIds.length === 1
						? (chipCompany?.name ??
							(companyIds[0] === null ? "No linked company" : ""))
						: companyIds.includes(null)
							? `No linked company and ${companyIds.length - 1} linked company${
									companyIds.length - 1 > 1 ? "s" : ""
								}`
							: `${companyIds.length} companies`,
				onClear: clearCompanies,
				popoverContent: renderEditor("Companies"),
			})
		}
		if (personIds.length > 0) {
			result.push({
				key: "People",
				label: "People",
				...negationProps("personIds", personIds.length),
				valueText:
					personIds.length === 1
						? (!chipPerson?.isDeleted && chipPerson?.name) ||
							(!chipPerson?.isDeleted && chipPerson?.email) ||
							(personIds[0] === null ? "No linked persons" : "")
						: personIds.includes(null)
							? `No linked person and ${personIds.length - 1} linked person${
									personIds.length - 1 > 1 ? "s" : ""
								}`
							: `${personIds.length} persons`,
				onClear: clearPersons,
				popoverContent: renderEditor("People"),
			})
		}
		// Projects, tags and issues carry no is / is not operator: Zero's client
		// cannot run the not(exists()) that a junction relation would need.
		if (projectIds.length > 0) {
			result.push({
				key: "Projects",
				label: "Projects",
				valueCount: projectIds.length,
				valueText:
					projectIds.length === 1
						? (projects.find(({ id }) => id === projectIds[0])?.name ?? "")
						: `${projectIds.length} projects`,
				onClear: clearProjects,
				popoverContent: renderEditor("Projects"),
			})
		}
		if (tagIds.length > 0) {
			result.push({
				key: "Tags",
				label: "Tags",
				valueCount: tagIds.length,
				valueText:
					tagIds.length === 1
						? (tags.find(({ id }) => id === tagIds[0])?.name ?? "")
						: `${tagIds.length} tags`,
				onClear: clearTags,
				popoverContent: renderEditor("Tags"),
			})
		}
		if (issueIds.length > 0) {
			result.push({
				key: "Issues",
				label: "Issues",
				valueCount: issueIds.length,
				valueText:
					issueIds.length === 1
						? issueIds[0] === null
							? "No issue linked"
							: (chipIssue?.title ?? "")
						: `${issueIds.length} issues`,
				onClear: clearIssues,
				popoverContent: renderEditor("Issues"),
			})
		}
		if (assigneeIds.length > 0) {
			result.push({
				key: "Assignees",
				label: "Assignees",
				...negationProps("assigneeIds", assigneeIds.length),
				valueText:
					assigneeIds.length === 1
						? (users.find((u) => u.id === assigneeIds[0])?.name ??
							(assigneeIds[0] === null ? "Unassigned" : ""))
						: assigneeIds.includes(null)
							? `Unassigned and ${assigneeIds.length - 1} assignee${
									assigneeIds.length - 1 > 1 ? "s" : ""
								}`
							: `${assigneeIds.length} assignees`,
				onClear: clearAssignees,
				popoverContent: renderEditor("Assignees"),
			})
		}
		if (creatorIds.length > 0) {
			result.push({
				key: "Creators",
				label: "Creators",
				...negationProps("creatorIds", creatorIds.length),
				valueText:
					creatorIds.length === 1
						? (users.find((u) => u.id === creatorIds[0])?.name ?? "")
						: `${creatorIds.length} creators`,
				onClear: clearCreators,
				popoverContent: renderEditor("Creators"),
			})
		}
		if (painLevels.length > 0) {
			result.push({
				key: "Priorities",
				label: "Priorities",
				...negationProps("painLevels", painLevels.length),
				valueText:
					painLevels.length === 1
						? PriorityNaming[painLevels[0]!]
						: `${painLevels.length} priorities`,
				onClear: clearPainLevels,
				popoverContent: renderEditor("Priorities"),
			})
		}
		if (origins.length > 0) {
			const dedupedCount =
				origins.length -
				(origins.includes("SLACK") && origins.includes("SLACK_CONNECT")
					? 1
					: 0) -
				(origins.includes("INTERCOM") && origins.includes("INTERCOM_ATTACHMENT")
					? 1
					: 0)
			result.push({
				key: "Origins",
				label: "Origins",
				...negationProps("origins", origins.length),
				valueText:
					origins.length === 1
						? FeedbackOriginTextRecord[origins[0]!]
						: origins.length === 2 &&
							  ((origins.includes("SLACK") &&
									origins.includes("SLACK_CONNECT")) ||
									(origins.includes("INTERCOM") &&
										origins.includes("INTERCOM_ATTACHMENT")))
							? FeedbackOriginTextRecord[origins[0]!]
							: `${dedupedCount} origins`,
				onClear: clearOrigins,
				popoverContent: renderEditor("Origins"),
			})
		}
		if (portalInstanceIds.length > 0) {
			const onlyId = portalInstanceIds[0]
			result.push({
				key: "Portals",
				label: "Portals",
				...negationProps("portalInstanceIds", portalInstanceIds.length),
				valueText:
					portalInstanceIds.length === 1
						? onlyId
							? (portalMarkById.get(onlyId)?.name ?? "")
							: MAIN_PORTAL_LABEL
						: `${portalInstanceIds.length} portals`,
				onClear: clearPortalInstances,
				popoverContent: renderEditor("Portals"),
			})
		}
		if (slackWorkspaceIds.length > 0) {
			result.push({
				key: "SlackWorkspaces",
				label: "Slack workspace",
				...negationProps("slackWorkspaceIds", slackWorkspaceIds.length),
				valueText:
					slackWorkspaceIds.length === 1
						? (slackWorkspaces.find((w) => w.id === slackWorkspaceIds[0])
								?.name ?? "")
						: `${slackWorkspaceIds.length} workspaces`,
				onClear: clearSlackWorkspaces,
				popoverContent: renderEditor("SlackWorkspaces"),
			})
		}
		if (slackChannelIds.length > 0) {
			const onlyChannel = slackChannelRows.find(
				(row) => row.channelId === slackChannelIds[0],
			)
			result.push({
				key: "SlackChannels",
				label: "Slack channel",
				...negationProps("slackChannelIds", slackChannelIds.length),
				valueText:
					slackChannelIds.length === 1
						? onlyChannel
							? `#${onlyChannel.name}`
							: ""
						: `${slackChannelIds.length} channels`,
				onClear: clearSlackChannels,
				popoverContent: renderEditor("SlackChannels"),
			})
		}
		if (emailAddressIds.length > 0) {
			const onlyId = emailAddressIds[0]
			result.push({
				key: "Inboxes",
				label: "Inbox",
				...negationProps("emailAddressIds", emailAddressIds.length),
				valueText:
					emailAddressIds.length === 1
						? (inboxAddresses.find((address) => address.id === onlyId)
								?.primaryEmail ?? "1 inbox")
						: `${emailAddressIds.length} inboxes`,
				onClear: clearEmailAddresses,
				popoverContent: renderEditor("Inboxes"),
			})
		}
		if (statuses.length > 0) {
			result.push({
				key: "Statuses",
				label: "Statuses",
				...negationProps("statuses", statuses.length),
				valueText:
					statuses.length === 1
						? StatusNaming[statuses[0]!]
						: `${statuses.length} statuses`,
				onClear: clearStatuses,
				popoverContent: renderEditor("Statuses"),
			})
		}
		if (replyStates.length > 0) {
			result.push({
				key: "ReplyStates",
				label: "Response",
				...negationProps("replyStates", replyStates.length),
				valueText:
					replyStates.length === 1
						? ReplyStateNaming[replyStates[0]!]
						: `${replyStates.length} responses`,
				onClear: clearReplyStates,
				popoverContent: renderEditor("ReplyStates"),
			})
		}
		if (issueStatuses.length > 0) {
			result.push({
				key: "IssueStatuses",
				label: "Issue status",
				valueCount: issueStatuses.length,
				valueText:
					issueStatuses.length === 1
						? issueStatuses[0]!
						: `${issueStatuses.length} statuses`,
				onClear: clearIssueStatuses,
				popoverContent: renderEditor("IssueStatuses"),
			})
		}
		if (companyTiers.length > 0) {
			result.push({
				key: "CompanyTiers",
				label: "CompanyTiers",
				...negationProps("companyTiers", companyTiers.length),
				valueText:
					companyTiers.length === 1
						? companyTiers[0]!
						: `${companyTiers.length} tiers`,
				onClear: clearCompanyTiers,
				popoverContent: renderEditor("CompanyTiers"),
			})
		}
		if (companyStatuses.length > 0) {
			result.push({
				key: "CompanyStatuses",
				label: "CompanyStatuses",
				...negationProps("companyStatuses", companyStatuses.length),
				valueText:
					companyStatuses.length === 1
						? companyStatuses[0]!
						: `${companyStatuses.length} statuses`,
				onClear: clearCompanyStatuses,
				popoverContent: renderEditor("CompanyStatuses"),
			})
		}
		if (companyOwnerIds.length > 0) {
			result.push({
				key: "CompanyOwners",
				label: "CompanyOwners",
				...negationProps("companyOwnerIds", companyOwnerIds.length),
				valueText:
					companyOwnerIds.length === 1
						? companyOwnerIds[0] === null
							? "No owner"
							: "1 owner"
						: `${companyOwnerIds.length} owners`,
				onClear: clearCompanyOwners,
				popoverContent: renderEditor("CompanyOwners"),
			})
		}

		return result
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		companyIds,
		personIds,
		projectIds,
		tagIds,
		issueIds,
		assigneeIds,
		creatorIds,
		painLevels,
		origins,
		portalInstanceIds,
		portalMarkById,
		slackWorkspaceIds,
		slackWorkspaces,
		slackChannelIds,
		slackChannelRows,
		emailAddressIds,
		inboxAddresses,
		statuses,
		replyStates,
		companyTiers,
		companyStatuses,
		companyOwnerIds,
		issueStatuses,
		negations,
		chipCompany,
		chipPerson,
		projects,
		tags,
		chipIssue,
		users,
		query,
	])

	const handleSaveAsView = useCallback(() => {
		const id = generateId()
		const slug = generateId().replace(/-/g, "").slice(0, 12)
		zero.mutate(
			mutators.customViews.create({
				id,
				name: "New view",
				slug,
				icon: null,
				filters: {
					negations,
					companyIds,
					personIds,
					issueIds,
					tagIds,
					projectIds,
					assigneeIds,
					origins,
					portalInstanceIds,
					slackWorkspaceIds,
					slackChannelIds,
					emailAddressIds,
					painLevels,
					creatorIds,
					statuses,
					replyStates,
					companyTiers,
					companyStatuses,
					companyOwnerIds,
					issueStatuses,
					companyRevenue,
					companySize,
				},
				sortOrder: customViews.length,
			}),
		)
		setActiveCustomViewId(id)
		setInboxState("custom-view")
		setPendingEditCustomViewId(id)
		void navigate({ to: "/threads", replace: true })
	}, [
		zero,
		customViews.length,
		negations,
		companyIds,
		personIds,
		issueIds,
		tagIds,
		projectIds,
		assigneeIds,
		origins,
		portalInstanceIds,
		slackWorkspaceIds,
		slackChannelIds,
		emailAddressIds,
		painLevels,
		creatorIds,
		statuses,
		replyStates,
		companyTiers,
		companyStatuses,
		companyOwnerIds,
		issueStatuses,
		companyRevenue,
		companySize,
		setActiveCustomViewId,
		setInboxState,
		setPendingEditCustomViewId,
		navigate,
	])

	const numericChipExists = companyRevenue !== null || companySize !== null
	const anyFilterApplied = chips.length > 0 || numericChipExists
	if (!anyFilterApplied) return null
	if (!embedded && isEditingCustomView) return null

	const tail = (
		<>
			{companyRevenue && (
				<div className="flex items-center divide-x divide-gray-300 overflow-hidden whitespace-nowrap rounded-lg border border-gray-300 bg-white text-xs dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
					<span className="px-2 py-1 text-primary">Company revenue</span>
					<span className="truncate px-2 py-1 text-primary">
						{companyRevenue.operator} ${companyRevenue.value} ARR
					</span>
					<button
						type="button"
						className="flex items-center self-stretch px-2 text-tertiary hover:bg-gray-150 hover:text-primary dark:hover:bg-gray-750"
						onClick={() => setCompanyRevenue(null)}
					>
						<X className="h-3 w-3" />
					</button>
				</div>
			)}
			{companySize && (
				<div className="flex items-center divide-x divide-gray-300 overflow-hidden whitespace-nowrap rounded-lg border border-gray-300 bg-white text-xs dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
					<span className="px-2 py-1 text-primary">Company size</span>
					<span className="truncate px-2 py-1 text-primary">
						{companySize.operator} {companySize.value}
					</span>
					<button
						type="button"
						className="flex items-center self-stretch px-2 text-tertiary hover:bg-gray-150 hover:text-primary dark:hover:bg-gray-750"
						onClick={() => setCompanySize(null)}
					>
						<X className="h-3 w-3" />
					</button>
				</div>
			)}
			{!embedded && inboxState !== "custom-view" && (
				<Button className="ml-auto" type="button" onClick={handleSaveAsView}>
					Save
				</Button>
			)}
		</>
	)

	return (
		<FilterControlChips
			chips={chips}
			tailContent={tail}
			containerClassName={
				embedded
					? "flex cursor-default select-none flex-wrap items-center gap-2"
					: undefined
			}
		/>
	)
})
